import { router } from '@inertiajs/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatDateTime, formatNumber } from '../format'
import type { CostEvent, Pagination } from '../types'

interface Props {
  events: CostEvent[]
  pagination: Pagination
}

export default function CostEventsTable({ events, pagination }: Props) {
  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.get(`/cc/litellm_costs?${params.toString()}`, {}, { preserveState: true, preserveScroll: true })
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-auto max-h-[calc(100vh-430px)]">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Horário</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Worker</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Modelo</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Custo</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Tokens</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Request</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {events.map((event) => (
              <tr key={event.id} className="hover:bg-accent transition-colors">
                <td className="px-4 py-3 text-sm whitespace-nowrap text-foreground">{formatDateTime(event.requestStartedAt)}</td>
                <td className="px-4 py-3">
                  <div className="text-sm text-foreground">{event.workerName || event.instanceName}</div>
                  <div className="text-xs text-muted-foreground">{event.organizationName || '—'} · {event.instanceName}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-foreground">{event.model || '—'}</div>
                  <div className="text-xs text-muted-foreground">{event.provider || event.litellmKeyAlias || '—'}</div>
                </td>
                <td className="px-4 py-3 text-sm text-right font-medium text-foreground">{formatCurrency(event.spendUsd)}</td>
                <td className="px-4 py-3 text-sm text-right text-foreground">
                  <div>{formatNumber(event.totalTokens)}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatNumber(event.promptTokens)} / {formatNumber(event.completionTokens)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{event.status || '—'}</td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-muted-foreground max-w-56 truncate">{event.requestId || event.id}</div>
                  <div className="font-mono text-[10px] text-muted-foreground max-w-56 truncate">{event.litellmTeamId || '—'}</div>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nenhum custo encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
        <span className="text-sm text-muted-foreground">
          Página {pagination.page} de {pagination.totalPages} · {formatNumber(pagination.total)} eventos
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => goToPage(pagination.page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
